import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import logo from "../assets/logo.png";
import "../styles/components/Navbar.css";
import { useLang } from "../context/LangContext";

const AUTO_CLOSE_MS = 15000;
const DESKTOP_BREAKPOINT = 992;

function isValidHash(hash) {
  return typeof hash === "string" && hash.startsWith("#") && hash.length > 1;
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeHash, setActiveHash] = useState("");

  const navRef = useRef(null);
  const inactivityTimerRef = useRef(null);
  const scrollRafRef = useRef(null);
  const scrollStateRef = useRef(false);

  const { lang, setLang, dict } = useLang();
  const navDict = useMemo(() => dict?.nav ?? {}, [dict]);

  const clearInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) {
      window.clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
  }, []);

  const closeMenu = useCallback(() => {
    clearInactivityTimer();
    setOpen(false);
  }, [clearInactivityTimer]);

  const startInactivityTimer = useCallback(() => {
    clearInactivityTimer();
    inactivityTimerRef.current = window.setTimeout(() => {
      setOpen(false);
    }, AUTO_CLOSE_MS);
  }, [clearInactivityTimer]);

  const config = useMemo(
    () => ({
      labels: {
        home: navDict.home ?? "",
        solutions: navDict.solutions ?? "",
        useCases: navDict.useCases ?? "",
        about: navDict.about ?? "",
        ourMilestones: navDict.ourMilestones ?? navDict.OurMilestones ?? "",
        collaborators: navDict.collaborators ?? "",
        ourTeam: navDict.ourTeam ?? navDict.OurTeam ?? "",
        contact: navDict.contact ?? "",
        cta: navDict.cta ?? "",
        logoAlt: navDict.logoAlt ?? "Logo",
        ariaLogo: navDict.ariaLogo ?? "Ana sayfa",
        ariaToggle: navDict.ariaToggle ?? "Menüyü aç",
        ariaPrimary: navDict.ariaPrimary ?? "Ana navigasyon",
        ariaLanguage: navDict.ariaLanguage ?? "Dil seçimi",
        ariaMobileMenu: navDict.ariaMobileMenu ?? "Mobil menü",
      },
      hashes: {
        home: navDict.homeHash ?? "#hero",
        solutions: navDict.solutionsHash ?? "#solutions",
        useCases: navDict.useCasesHash ?? "#use-cases",
        about: navDict.aboutHash ?? "#about",
        ourMilestones:
          navDict.ourMilestonesHash ??
          navDict.OurMilestonesHash ??
          "#our-milestones",
        collaborators: navDict.collaboratorsHash ?? "#collaborators",
        ourTeam: navDict.ourTeamHash ?? navDict.OurTeamHash ?? "#our-team",
        contact: navDict.contactHash ?? "#contact",
      },
    }),
    [navDict]
  );

  const navItems = useMemo(
    () =>
      [
        {
          key: "home",
          label: config.labels.home,
          hash: config.hashes.home,
        },
        {
          key: "solutions",
          label: config.labels.solutions,
          hash: config.hashes.solutions,
        },
        {
          key: "useCases",
          label: config.labels.useCases,
          hash: config.hashes.useCases,
        },
        {
          key: "about",
          label: config.labels.about,
          hash: config.hashes.about,
        },
        {
          key: "ourMilestones",
          label: config.labels.ourMilestones,
          hash: config.hashes.ourMilestones,
        },
        {
          key: "collaborators",
          label: config.labels.collaborators,
          hash: config.hashes.collaborators,
        },
        {
          key: "ourTeam",
          label: config.labels.ourTeam,
          hash: config.hashes.ourTeam,
        },
      ].filter((item) => item.label && isValidHash(item.hash)),
    [config]
  );

  const cleanHref = useCallback(() => window.location.pathname || "/", []);

  const getNavOffset = useCallback(() => {
    const navHeight = navRef.current?.offsetHeight ?? 66;
    const topOffset = 10;
    const extraOffset = 10;
    return navHeight + topOffset + extraOffset;
  }, []);

  const scrollToHash = useCallback(
    (hash, options = {}) => {
      const { behavior = "smooth" } = options;

      if (!isValidHash(hash)) return false;

      const target = document.querySelector(hash);

      if (!target) {
        setActiveHash(hash);
        return false;
      }

      const targetTop =
        target.getBoundingClientRect().top + window.scrollY - getNavOffset();

      window.scrollTo({
        top: Math.max(0, targetTop),
        left: 0,
        behavior,
      });

      if (window.location.search || window.location.hash) {
        window.history.replaceState({}, "", window.location.pathname);
      }

      setActiveHash(hash);
      return true;
    },
    [getNavOffset]
  );

  const handleLanguageChange = useCallback(
    (nextLang) => {
      if (!nextLang || nextLang === lang) return;

      closeMenu();
      setLang(nextLang);

      const currentActiveHash = activeHash || config.hashes.home || "#hero";

      requestAnimationFrame(() => {
        const target = document.querySelector(currentActiveHash);
        if (!target) return;

        const targetTop =
          target.getBoundingClientRect().top +
          window.scrollY -
          getNavOffset();

        window.scrollTo({
          top: Math.max(0, targetTop),
          left: 0,
          behavior: "auto",
        });

        if (window.location.search || window.location.hash) {
          window.history.replaceState({}, "", window.location.pathname);
        }

        setActiveHash(currentActiveHash);
      });
    },
    [activeHash, closeMenu, config.hashes.home, getNavOffset, lang, setLang]
  );

  useEffect(() => {
    return () => {
      clearInactivityTimer();
      if (scrollRafRef.current) {
        window.cancelAnimationFrame(scrollRafRef.current);
      }
    };
  }, [clearInactivityTimer]);

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth >= DESKTOP_BREAKPOINT && open) {
        closeMenu();
      }
    };

    window.addEventListener("resize", onResize, { passive: true });
    return () => window.removeEventListener("resize", onResize);
  }, [open, closeMenu]);

  useEffect(() => {
    const updateScrolled = () => {
      scrollRafRef.current = null;
      const nextScrolled = window.scrollY > 14;

      if (scrollStateRef.current !== nextScrolled) {
        scrollStateRef.current = nextScrolled;
        setIsScrolled(nextScrolled);
      }
    };

    const onScroll = () => {
      if (scrollRafRef.current) return;
      scrollRafRef.current = window.requestAnimationFrame(updateScrolled);
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", onScroll);
      if (scrollRafRef.current) {
        window.cancelAnimationFrame(scrollRafRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const homeHash = config.hashes.home || "#hero";
    setActiveHash(homeHash);

    const timer = window.setTimeout(() => {
      if (window.location.search || window.location.hash) {
        window.history.replaceState({}, "", window.location.pathname);
      }

      const target = document.querySelector(homeHash);
      if (!target) return;

      const targetTop =
        target.getBoundingClientRect().top + window.scrollY - getNavOffset();

      window.scrollTo({
        top: Math.max(0, targetTop),
        left: 0,
        behavior: "auto",
      });
    }, 50);

    return () => window.clearTimeout(timer);
  }, [config.hashes.home, getNavOffset]);

  useEffect(() => {
    const elements = navItems
      .map((item) => {
        const el = document.querySelector(item.hash);
        return el ? { hash: item.hash, el } : null;
      })
      .filter(Boolean);

    if (!elements.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visibleEntries = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);

        if (visibleEntries.length > 0) {
          const visibleHash = `#${visibleEntries[0].target.id}`;
          setActiveHash(visibleHash);

          if (window.location.search || window.location.hash) {
            window.history.replaceState({}, "", window.location.pathname);
          }
        }
      },
      {
        root: null,
        rootMargin: "-18% 0px -58% 0px",
        threshold: [0.2, 0.35, 0.5, 0.7],
      }
    );

    elements.forEach((item) => observer.observe(item.el));

    return () => observer.disconnect();
  }, [navItems]);

  useEffect(() => {
    if (!open) {
      clearInactivityTimer();
      return;
    }

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    startInactivityTimer();

    const handlePointerDown = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        closeMenu();
        return;
      }

      startInactivityTimer();
    };

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        closeMenu();
        return;
      }

      startInactivityTimer();
    };

    const handleFocusIn = (e) => {
      if (navRef.current && navRef.current.contains(e.target)) {
        startInactivityTimer();
      }
    };

    const handleScrollWhileOpen = () => {
      startInactivityTimer();
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("focusin", handleFocusIn);
    window.addEventListener("scroll", handleScrollWhileOpen, {
      passive: true,
    });

    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("focusin", handleFocusIn);
      window.removeEventListener("scroll", handleScrollWhileOpen);
      clearInactivityTimer();
    };
  }, [open, startInactivityTimer, closeMenu, clearInactivityTimer]);

  const handleNavClick = useCallback(
    (e, hash) => {
      e.preventDefault();
      closeMenu();
      scrollToHash(hash);
    },
    [closeMenu, scrollToHash]
  );

  const handleToggle = useCallback(() => {
    setOpen((prev) => !prev);
  }, []);

  const renderNavLink = useCallback(
    (item, extraClass = "") => {
      if (!item?.label || !isValidHash(item.hash)) return null;

      return (
        <a
          key={item.key}
          href={cleanHref()}
          className={[
            "navbar-link",
            activeHash === item.hash ? "is-active" : "",
            extraClass,
          ]
            .filter(Boolean)
            .join(" ")}
          onClick={(e) => handleNavClick(e, item.hash)}
          aria-current={activeHash === item.hash ? "location" : undefined}
        >
          {item.label}
        </a>
      );
    },
    [activeHash, cleanHref, handleNavClick]
  );

  return (
    <header className={`navbar-wrap ${isScrolled ? "is-scrolled" : ""}`}>
      <nav
        className={`navbar ${isScrolled ? "is-scrolled" : ""}`}
        ref={navRef}
        aria-label={config.labels.ariaPrimary}
      >
        <span className="navbar-aura" aria-hidden="true" />

        <a
          href={cleanHref()}
          className="navbar-logo"
          aria-label={config.labels.ariaLogo}
          onClick={(e) => handleNavClick(e, config.hashes.home)}
        >
          <span className="navbar-logo-badge">
            <img src={logo} alt={config.labels.logoAlt} />
          </span>
        </a>

        <div className="navbar-right">
          <div className="navbar-links desktop-only">
            {navItems.map((item) => renderNavLink(item))}
          </div>

          <div
            className="navbar-lang"
            role="group"
            aria-label={config.labels.ariaLanguage}
          >
            <button
              type="button"
              className={`lang-btn ${lang === "tr" ? "active" : ""}`}
              aria-pressed={lang === "tr"}
              onClick={() => handleLanguageChange("tr")}
            >
              TR
            </button>

            <button
              type="button"
              className={`lang-btn ${lang === "en" ? "active" : ""}`}
              aria-pressed={lang === "en"}
              onClick={() => handleLanguageChange("en")}
            >
              EN
            </button>
          </div>

          {!!config.labels.cta && (
            <a
              href={cleanHref()}
              className="navbar-cta desktop-only"
              onClick={(e) => handleNavClick(e, config.hashes.contact)}
            >
              {config.labels.cta}
            </a>
          )}

          <button
            type="button"
            className={`navbar-toggle ${open ? "is-active" : ""}`}
            aria-label={config.labels.ariaToggle}
            aria-expanded={open}
            aria-controls="mobile-menu"
            onClick={handleToggle}
          >
            <span className="bar" aria-hidden="true" />
            <span className="bar" aria-hidden="true" />
            <span className="bar" aria-hidden="true" />
          </button>
        </div>

        <div
          id="mobile-menu"
          className={`mobile-menu ${open ? "is-open" : ""}`}
          role="dialog"
          aria-modal="true"
          aria-label={config.labels.ariaMobileMenu}
        >
          <div className="mobile-menu__inner">
            {navItems.map((item) => renderNavLink(item))}
            {!!config.labels.cta && (
              <a
                href={cleanHref()}
                className="navbar-cta navbar-cta--mobile"
                onClick={(e) => handleNavClick(e, config.hashes.contact)}
              >
                {config.labels.cta}
              </a>
            )}
          </div>
        </div>
      </nav>
    </header>
  );
}